import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as PIXI from "pixi.js";
import { ASSETS } from "../../assets";
import { initializeStyles } from "../../utils/injectStyles";
import FXManager from "../../Engine/Effects/FXManager";
import { getRulesPath } from "../../utils/pathConfig";
import { sanitizeCrowdinRulesHtml } from "../../utils/sanitizeCrowdinRulesHtml";
import { FEATURES_CONFIG } from "../../featuresConfig";

export const loadAssets = createAsyncThunk(
  "assets/loadAssets",
  async (_, { rejectWithValue }) => {
    try {
      // Inject CSS styles and preload fonts
      await initializeStyles();

      // Enable mipmaps before loading any textures — must happen before PIXI.Assets.load()
      // Pre-generates smaller texture levels so downscaled sprites render smoothly
      // ⚠️ DISABLED: Global mipmap causes black background flashes on PIXI.Text (coin values).
      // TODO: Apply selectively to UI button/icon textures only.
      // PIXI.BaseTexture.defaultOptions.mipmap = PIXI.MIPMAP_MODES.ON;

      // Wait for all web fonts to load before proceeding
      // This ensures PixiJS Text components can use custom fonts immediately
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      } else {
        // Fallback: wait a bit for fonts to start loading
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // 🚀 PARALLEL LOADING - Load all textures simultaneously
      const texturePromises = ASSETS.textures.map(async (asset) => {
        try {
          // Add asset with alias so it can be retrieved by name
          PIXI.Assets.add({ alias: asset.name, src: asset.url });

          if (!PIXI.Assets.cache.has(asset.name)) {
            await PIXI.Assets.load(asset.name);
          }
        } catch (textureError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // 🚀 PARALLEL LOADING - Load all spine animations simultaneously
      const spinePromises = ASSETS.spineAnimations.map(async (animation) => {
        const baseName = animation.name;
        const jsonPath = animation.json;
        const atlasPath = animation.atlas || jsonPath.replace(".json", ".atlas");

        // Validate paths before attempting to load
        if (!jsonPath || !atlasPath || !baseName) {
          throw new Error(
            `Invalid spine configuration for ${baseName}: missing path or name`
          );
        }

        // Check for malformed paths (should not contain undefined, null, HTML, or empty segments)
        const invalidPatterns = [
          "undefined",
          "null",
          "<!DOCTYPE",
          "<html",
          "DOCTYPE html",
          "window.__OPTIONS__",
          "response.ok",
          "Call the session",
        ];

        const hasInvalidPattern = invalidPatterns.some(
          (pattern) =>
            jsonPath.includes(pattern) ||
            atlasPath.includes(pattern) ||
            baseName.includes(pattern)
        );

        if (
          hasInvalidPattern ||
          jsonPath.endsWith("/.json") ||
          atlasPath.endsWith("/.atlas") ||
          !jsonPath.endsWith(".json") ||
          !atlasPath.endsWith(".atlas")
        ) {
          throw new Error(`Invalid spine path for ${baseName}: ${jsonPath}`);
        }

        // Final validation: ensure paths are valid URLs and don't contain HTML
        try {
          // Validate URL format
          const testUrl = new URL(jsonPath, window.location.origin);
          if (!testUrl.pathname.endsWith(".json")) {
            throw new Error(`Invalid JSON path format: ${jsonPath}`);
          }
        } catch (urlError) {
          // If URL construction fails, it's likely a relative path - validate manually
          if (
            !jsonPath.startsWith("/") &&
            !jsonPath.startsWith("./") &&
            !jsonPath.startsWith("http")
          ) {
            throw new Error(`Invalid path format: ${jsonPath}`);
          }
        }

        try {
          // Skip if already loaded (e.g. logo spine pre-loaded by backport Loader)
          if (!PIXI.Assets.cache.has(`${baseName}-skeleton`)) {
            PIXI.Assets.add({ alias: `${baseName}-skeleton`, src: jsonPath });
            PIXI.Assets.add({ alias: `${baseName}-atlas`, src: atlasPath });
            await PIXI.Assets.load([
              `${baseName}-skeleton`,
              `${baseName}-atlas`,
            ]);
          }
        } catch (spineError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // 🚀 PARALLEL LOADING - Load all bitmap fonts simultaneously
      const fontPromises = ASSETS.bitmapFonts.map(async (font) => {
        try {
          if (!PIXI.Assets.cache.has(font.json)) {
            await PIXI.Assets.load(font.json);
          }
        } catch (fontError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // 🔗 SEQUENTIAL LOADING - RevoltFX effect bundles
      // All effects share a single FX instance inside FXManager.
      // First effect uses loadBundleFiles (RevoltFX's native loader).
      // Additional effects use loadAdditionalBundle (unique PIXI aliases
      // to bypass RevoltFX's hardcoded rfx_* keys that cause cache collision).
      const effectsLoaded = (async () => {
        for (const effect of ASSETS.effects) {
          try {
            if (!FXManager.isReady) {
              // First effect — full init (creates FX instance + ticker)
              await FXManager.init(effect.bundle, effect.spritesheet);
            } else if (effect.name === "particle_trail_effect") {
              // Trail particles — needs its own FX instance (self-contained bundle)
              await FXManager.initTrailBundle(effect.bundle, effect.spritesheet);
            } else {
              // Additional effects — load into same FX instance
              await FXManager.loadAdditionalBundle(
                effect.bundle,
                effect.spritesheet,
                effect.name,
              );
            }
          } catch (effectError) {
            // Effect assets are optional — game continues without particle effects
          }
        }

        // Warm particle pools off-screen while the loading screen is still up, so
        // the first bolt/trail of the bonus doesn't pay the cold-start (empty pools).
        // Best-effort — never blocks load.
        try {
          await FXManager.warmUpPools();
        } catch (warmupError) {
          // Warmup is optional — ignore failures
        }
      })();

      // 🚀 PARALLEL LOADING - Prefetch game rules HTML
      const rulesLoaded = (async () => {
        try {
          const locale = window.__OPTIONS__?.locale || "en";
          const fallbackLng = "en";
          const MAX_WIN_MULTIPLIER = FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER || FEATURES_CONFIG.HOLD_AND_WIN?.MAX_WIN_MULTIPLIER || 10000;
          const rtpValue = window.__OPTIONS__?.math?.rtp?.main || "";

          let response = await fetch(getRulesPath(locale, fallbackLng));
          if (!response.ok) {
            response = await fetch(getRulesPath(fallbackLng, fallbackLng));
          }
          if (!response.ok) return null;

          let html = await response.text();
          html = sanitizeCrowdinRulesHtml(html);
          html = html.replace(/{MAX_MULTIPLIER}/g, MAX_WIN_MULTIPLIER.toLocaleString());
          html = html.replace(/{RTP_VALUE}/g, rtpValue.toString());
          return html;
        } catch {
          return null;
        }
      })();

      // Wait for ALL assets to finish loading (happens in parallel!)
      // Note: Failed assets are handled gracefully - game continues without them
      // Failed assets return null, but we don't need to count them for production
      const allResults = await Promise.all([
        ...texturePromises,
        ...spinePromises,
        ...fontPromises,
        effectsLoaded,
        rulesLoaded,
      ]);

      // rulesLoaded is always the last promise in the array
      const rulesHtml = allResults[allResults.length - 1];


      return { rulesHtml: rulesHtml || "" };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const assetsSlice = createSlice({
  name: "assets",
  initialState: {
    status: "idle",
    error: null,
    rulesHtml: "",
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAssets.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loadAssets.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload?.rulesHtml) {
          state.rulesHtml = action.payload.rulesHtml;
        }
      })
      .addCase(loadAssets.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "An unknown error occurred.";
      });
  },
});

export const selectRulesHtml = (state) => state.assets.rulesHtml;

export default assetsSlice.reducer;
